import type { ValidationError } from "../../types";
import ErrorText from "./ErrorText";
import { IconButton } from "../Button/Button";
import { Help } from "../Icons";
import { forwardRef, ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "../../contexts/Translation";
import styles from "./Input.module.css";

type SelectOption = {
  text: string;
  value: string;
};

type BaseInputProps = {
  name: string;
  value?: string | number | undefined | boolean;
  type?: string;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  readOnly?: boolean;
  helpText?: string | ReactNode;
  options?: SelectOption[];
  infoText?: string | ReactNode;
  handleChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  handleBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  error?: ValidationError | null;
  className?: string;
  fieldClassName?: string;
  labelClassName?: string;
  infoClassName?: string;
};

type HelpTextProps = {
  text: ReactNode | string | undefined;
  isOpen: boolean;
  id: string;
  onClose: () => void;
};

type HelpIconProps = {
  text?: ReactNode | string | undefined;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  controls: string;
};

const getContainerClassName = (error?: ValidationError | null, className?: string) =>
  [styles.container, error && styles.containerError, className].filter(Boolean).join(" ");

const getFieldClassName = (error?: ValidationError | null, fieldClassName?: string, type?: string) =>
  [styles.field, type === "textarea" && styles.textarea, type === "color" && styles.colorField, error && styles.fieldError, fieldClassName]
    .filter(Boolean)
    .join(" ");

export const HelpText = ({ text, isOpen, id, onClose }: HelpTextProps) => {
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (boxRef.current?.contains(target) || target.closest?.("button")?.getAttribute("aria-controls") === id) {
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isOpen, id, onClose]);

  if (!text || !isOpen) {
    return null;
  }

  return (
    <div className={styles.helpBox} id={id} ref={boxRef} role='note'>
      <div className={styles.helpIconWrap}>
        <Help />
      </div>
      <div>{text}</div>
    </div>
  );
};

export const HelpIcon = ({ text, isOpen, setIsOpen, controls }: HelpIconProps) => {
  const t = useTranslations("misc");
  if (!text) {
    return null;
  }

  return (
    <IconButton
      variant='inline'
      active={isOpen}
      ariaControls={controls}
      ariaExpanded={isOpen}
      onClick={() => setIsOpen(!isOpen)}
      title={t("moreInfo")}
    >
      <Help />
    </IconButton>
  );
};

const Input = forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      infoText,
      helpText,
      name,
      value,
      type = "text",
      label,
      placeholder,
      maxLength,
      readOnly = false,
      handleChange,
      handleBlur,
      error,
      className,
      fieldClassName,
      labelClassName,
      infoClassName,
    },
    ref,
  ) => {
    const [helpOpen, setHelpOpen] = useState(false);
    const helpId = useId();
    const closeHelp = useCallback(() => setHelpOpen(false), []);
    const normalizedValue = value === undefined ? undefined : `${value}`;

    if (type === "checkbox") {
      return (
        <div className={getContainerClassName(error, className)}>
          <label className={[styles.checkboxLabel, labelClassName].filter(Boolean).join(" ")} htmlFor={name}>
            <input
              checked={value === 1 || value === "1" || value === true}
              className={[styles.checkbox, fieldClassName].filter(Boolean).join(" ")}
              disabled={readOnly}
              id={name}
              name={name}
              onChange={handleChange}
              ref={ref}
              type='checkbox'
              value='1'
            />
            <span>{label}</span>
          </label>
          <ErrorText error={error} />
          {infoText && <div className={[styles.infoText, infoClassName].filter(Boolean).join(" ")}>{infoText}</div>}
        </div>
      );
    }

    return (
      <div className={getContainerClassName(error, className)}>
        {label && (
          <label className={[styles.label, labelClassName].filter(Boolean).join(" ")} htmlFor={name}>
            <span>{label}</span>
            <HelpIcon text={helpText} isOpen={helpOpen} setIsOpen={setHelpOpen} controls={helpId} />
          </label>
        )}
        <HelpText text={helpText} isOpen={helpOpen} id={helpId} onClose={closeHelp} />

        {type === "textarea" ? (
          <textarea
            className={getFieldClassName(error, fieldClassName, type)}
            id={name}
            maxLength={maxLength}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={placeholder}
            readOnly={readOnly}
            value={normalizedValue}
          />
        ) : (
          <input
            className={getFieldClassName(error, fieldClassName, type)}
            id={name}
            maxLength={maxLength}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            readOnly={readOnly}
            ref={ref}
            type={type}
            value={normalizedValue}
          />
        )}

        <ErrorText error={error} />
        {infoText && <div className={[styles.infoText, infoClassName].filter(Boolean).join(" ")}>{infoText}</div>}
      </div>
    );
  },
);

export default Input;

export const Select = ({
  helpText,
  options = [],
  readOnly,
  infoText,
  name,
  value = "",
  label,
  handleChange,
  handleBlur,
  error,
  className,
  fieldClassName,
  labelClassName,
  infoClassName,
}: BaseInputProps) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  const normalizedValue = value === undefined ? undefined : `${value}`;

  return (
    <div className={getContainerClassName(error, className)}>
      {label && (
        <label className={[styles.label, labelClassName].filter(Boolean).join(" ")} htmlFor={name}>
          <span>{label}</span>
          <HelpIcon text={helpText} isOpen={helpOpen} setIsOpen={setHelpOpen} controls={helpId} />
        </label>
      )}
      <HelpText text={helpText} isOpen={helpOpen} id={helpId} onClose={closeHelp} />

      <select
        className={[styles.field, styles.select, error && styles.fieldError, fieldClassName].filter(Boolean).join(" ")}
        disabled={readOnly}
        id={name}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        value={normalizedValue}
      >
        {options.map((option, index) => (
          <option key={`${option.value}-${index}`} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>

      <ErrorText error={error} />
      {infoText && <div className={[styles.infoText, infoClassName].filter(Boolean).join(" ")}>{infoText}</div>}
    </div>
  );
};

export const RadioGroup = ({
  helpText,
  options = [],
  readOnly,
  infoText,
  name,
  value = "",
  label,
  handleChange,
  error,
  className,
  labelClassName,
  infoClassName,
}: BaseInputProps) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  return (
    <div className={getContainerClassName(error, className)}>
      {label && (
        <div className={[styles.label, labelClassName].filter(Boolean).join(" ")}>
          <span>{label}</span>
          <HelpIcon text={helpText} isOpen={helpOpen} setIsOpen={setHelpOpen} controls={helpId} />
        </div>
      )}
      <HelpText text={helpText} isOpen={helpOpen} id={helpId} onClose={closeHelp} />

      <div className={styles.radioGroup}>
        {options.map((option, index) => (
          <label className={styles.radioLabel} key={`radio-${index}`}>
            <input
              checked={value === option.value}
              className={styles.radio}
              disabled={readOnly}
              name={name}
              onChange={handleChange}
              type='radio'
              value={`${option.value}`}
            />
            <span>{option.text}</span>
          </label>
        ))}
      </div>

      <ErrorText error={error} />
      {infoText && <div className={[styles.infoText, infoClassName].filter(Boolean).join(" ")}>{infoText}</div>}
    </div>
  );
};
